import { useState, useEffect } from "react";

/**
 * TogoDossierAdminPanel — Panneau d'administration du dossier Ministère togolais.
 * Liste des 7 documents, téléchargement individuel + ZIP complet + checklist de dépôt.
 */
export const TogoDossierAdminPanel = ({ API, pinInput }) => {
  const [sections, setSections] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [checkedItems, setCheckedItems] = useState({});
  const [previewDoc, setPreviewDoc] = useState(null); // {key, label, pdf_url}
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const pinEnc = encodeURIComponent(pinInput || 'SAFUAYE2024');

  const loadSettings = async () => {
    try {
      const r = await fetch(`${API}/admin/settings?pin=${pinEnc}`);
      const d = await r.json();
      setSettings(d);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { loadSettings(); }, []); // eslint-disable-line

  const saveSettings = async (patch) => {
    setSavingSettings(true);
    try {
      const r = await fetch(`${API}/admin/settings?pin=${pinEnc}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      const d = await r.json();
      setSettings(d.settings);
    } catch (e) { /* ignore */ }
    setSavingSettings(false);
  };

  const CHECKLIST = [
    { id: "lettre", label: "Lettre de déclaration timbrée à 500 FCFA (1 original + 2 copies)" },
    { id: "statuts", label: "Statuts (3 exemplaires)" },
    { id: "reglement", label: "Règlement Intérieur (3 exemplaires)" },
    { id: "pv", label: "PV AG Constitutive + liste des 25 participants signée (3 exemplaires)" },
    { id: "bureau", label: "Liste Bureau Exécutif avec signatures originales (3 exemplaires)" },
    { id: "pieces_id", label: "Photocopies pièces d'identité des 5 membres (2 par membre)" },
    { id: "timbre", label: "Timbre fiscal 500 FCFA apposé sur la lettre" },
    { id: "frais", label: "Frais de dossier : 30 000 FCFA en espèces / chèque" },
    { id: "chemise", label: "Chemise à sangle contenant l'ensemble" },
    { id: "nif_next", label: "🧾 Après récépissé MATDDT → préparer le dossier OTR (voir document 10)" },
  ];

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/togo/dossier-list?pin=${encodeURIComponent(pinInput || 'SAFUAYE2024')}`);
      const data = await r.json();
      setSections(data.sections || []);
      setTotal(data.total || 0);
    } catch (e) { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const zipUrl = `${API}/togo/dossier-zip?pin=${encodeURIComponent(pinInput || 'SAFUAYE2024')}`;
  const printChecklist = () => {
    const w = window.open('', '_blank');
    if (!w) return;
    const rows = CHECKLIST.map((c, i) => `<tr><td style="width:30px;text-align:center;font-size:20px;">□</td><td style="padding:6px 12px;">${i + 1}. ${c.label}</td><td style="width:120px;border-bottom:1px solid #999;"></td></tr>`).join('');
    w.document.write(`<!doctype html><html><head><title>SAFUAYE-TOGO — Checklist de dépôt</title><style>body{font-family:sans-serif;padding:30px;} h1{color:#065f46;margin-bottom:6px;} table{width:100%;border-collapse:collapse;margin-top:20px;} td{border-bottom:1px dashed #ccc;padding:6px 4px;} .head{background:#065f46;color:#fff;font-weight:bold;} .foot{margin-top:40px;font-size:12px;color:#666;} .sig{display:flex;justify-content:space-between;margin-top:60px;} .sig div{border-top:1px solid #333;padding-top:6px;width:44%;font-size:12px;}</style></head><body onload="window.print()"><h1>📁 SAFUAYE-TOGO — Checklist de dépôt au MATGLAC</h1><p style="color:#374151;">Coche chaque pièce au moment du dépôt et fais tamponner par le guichet.</p><table><tr class="head"><td>✓</td><td>Pièce à déposer</td><td>Tampon</td></tr>${rows}</table><div class="sig"><div>Signature du déposant<br/><strong>M. AZIAKA KOFFI, Président</strong></div><div>Cachet du Ministère + date</div></div><p class="foot">SAFUAYE-TOGO · Loi n° 40-484 du 1er juillet 1901 · Siège : Atchrimi-Komé, Atakpamé</p></body></html>`);
    w.document.close();
  };

  return (
    <div data-testid="admin-togo-dossier-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="font-heading text-xl font-bold text-gray-900 flex items-center gap-2">
          🇹🇬 Dossier officiel SAFUAYE-TOGO — Ministère (MATGLAC)
        </h3>
        <div className="flex gap-2">
          <button onClick={load} className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-800 px-3 py-2 rounded font-semibold" data-testid="admin-togo-refresh">🔄 Rafraîchir</button>
          <button onClick={printChecklist} className="text-xs bg-white border border-emerald-300 text-emerald-800 hover:bg-emerald-50 px-3 py-2 rounded font-semibold" data-testid="admin-togo-print-checklist">🖨️ Imprimer checklist</button>
          <a href={zipUrl} className="text-xs bg-emerald-700 hover:bg-emerald-800 text-white px-3 py-2 rounded font-semibold" data-testid="admin-togo-download-zip">
            📦 Télécharger le dossier complet (ZIP)
          </a>
        </div>
      </div>

      <div className="bg-emerald-50 border-l-4 border-emerald-600 p-3 rounded mb-4 text-xs text-emerald-900">
        📋 <strong>Dossier de déclaration constitutive</strong> conforme aux exigences officielles du Ministère de l&apos;Administration Territoriale (Ve Division). Association 100 % togolaise régie par la loi n° 40-484 du 1er juillet 1901. Chaque PDF est signé par un QR-code renvoyant vers <code className="bg-white px-1 py-0.5 rounded">safuaye.org</code> pour la vérification publique.
      </div>

      {/* Suivi officiel — Récépissé MATDDT + NIF OTR */}
      {settings && (
        <div className="grid md:grid-cols-2 gap-3 mb-4" data-testid="admin-togo-official-status">
          <div className="bg-white border border-emerald-300 rounded-lg p-3">
            <h4 className="text-sm font-bold text-emerald-900 mb-2 flex items-center gap-2">
              🏛️ Récépissé MATDDT
              {settings.matddt_recepisse?.number && <span className="text-[10px] bg-emerald-600 text-white px-2 py-0.5 rounded-full">Obtenu</span>}
            </h4>
            <label className="block mb-2">
              <span className="text-[11px] font-semibold text-gray-700">N° du récépissé</span>
              <input
                type="text"
                placeholder="ex : 0234/MATDDT/DAPAT/2026"
                value={settings.matddt_recepisse?.number || ""}
                onChange={e => setSettings({ ...settings, matddt_recepisse: { ...settings.matddt_recepisse, number: e.target.value } })}
                onBlur={() => saveSettings({ matddt_recepisse: settings.matddt_recepisse })}
                className="mt-1 w-full text-sm border border-gray-300 rounded px-2 py-1"
                data-testid="admin-matddt-number"
              />
            </label>
            <label className="block">
              <span className="text-[11px] font-semibold text-gray-700">Date de délivrance</span>
              <input
                type="date"
                value={settings.matddt_recepisse?.date_delivrance || ""}
                onChange={e => setSettings({ ...settings, matddt_recepisse: { ...settings.matddt_recepisse, date_delivrance: e.target.value } })}
                onBlur={() => saveSettings({ matddt_recepisse: settings.matddt_recepisse })}
                className="mt-1 w-full text-sm border border-gray-300 rounded px-2 py-1"
                data-testid="admin-matddt-date"
              />
            </label>
          </div>

          <div className="bg-white border border-amber-300 rounded-lg p-3">
            <h4 className="text-sm font-bold text-amber-900 mb-2 flex items-center gap-2">
              🧾 NIF — Office Togolais des Recettes
              {settings.togo_nif?.number && <span className="text-[10px] bg-amber-600 text-white px-2 py-0.5 rounded-full">Attribué</span>}
              {!settings.togo_nif?.number && settings.matddt_recepisse?.number && (
                <span className="text-[10px] bg-blue-600 text-white px-2 py-0.5 rounded-full">Prêt à déposer</span>
              )}
            </h4>
            <label className="block mb-2">
              <span className="text-[11px] font-semibold text-gray-700">N° NIF (OTR)</span>
              <input
                type="text"
                placeholder="ex : 1000123456X"
                value={settings.togo_nif?.number || ""}
                onChange={e => setSettings({ ...settings, togo_nif: { ...settings.togo_nif, number: e.target.value } })}
                onBlur={() => saveSettings({ togo_nif: settings.togo_nif })}
                className="mt-1 w-full text-sm border border-gray-300 rounded px-2 py-1"
                data-testid="admin-nif-number"
              />
            </label>
            <label className="block">
              <span className="text-[11px] font-semibold text-gray-700">Date d'attribution</span>
              <input
                type="date"
                value={settings.togo_nif?.date_delivrance || ""}
                onChange={e => setSettings({ ...settings, togo_nif: { ...settings.togo_nif, date_delivrance: e.target.value } })}
                onBlur={() => saveSettings({ togo_nif: settings.togo_nif })}
                className="mt-1 w-full text-sm border border-gray-300 rounded px-2 py-1"
                data-testid="admin-nif-date"
              />
            </label>
            {savingSettings && <div className="text-[10px] text-emerald-700 mt-1">💾 Enregistrement…</div>}
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-500 text-center py-6">Chargement…</p>
      ) : (
        <div className="space-y-4 mb-6">
          {sections.map((s, sIdx) => (
            <div key={sIdx} className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <h4 className="font-heading text-sm font-bold text-slate-800 mb-2">{s.title} <span className="text-xs font-normal text-slate-500">({s.docs.length} document(s))</span></h4>
              <div className="grid md:grid-cols-2 gap-2">
                {s.docs.map(d => (
                  <div key={d.key} className="bg-white border border-gray-200 rounded p-2 flex items-start gap-3" data-testid={`admin-togo-doc-${d.key}`}>
                    <div className="text-xl">{d.icon}</div>
                    <div className="flex-1">
                      <div className="font-semibold text-sm text-gray-900">{d.label}</div>
                      <div className="text-[10px] text-gray-500 mt-1">{d.key} · {(d.pdf_size / 1024).toFixed(1)} KB</div>
                      <div className="mt-2 flex gap-2 flex-wrap">
                        {d.pdf_url && (
                          <button
                            type="button"
                            onClick={() => setPreviewDoc({ key: d.key, label: d.label, pdf_url: d.pdf_url })}
                            className="text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1 rounded font-semibold"
                            data-testid={`admin-togo-preview-${d.key}`}
                          >
                            👁️ Aperçu
                          </button>
                        )}
                        {d.pdf_url && (
                          <a href={d.pdf_url} target="_blank" rel="noreferrer" className="text-[11px] bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded font-semibold">
                            📄 PDF
                          </a>
                        )}
                        {d.docx_url && (
                          <a href={d.docx_url} target="_blank" rel="noreferrer" className="text-[11px] bg-sky-600 hover:bg-sky-700 text-white px-2 py-1 rounded font-semibold">
                            📝 DOCX
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div className="text-xs text-gray-500 italic">Total : {total} document(s) générés</div>
        </div>
      )}

      <div className="bg-white border border-amber-300 rounded-lg p-4" data-testid="admin-togo-checklist">
        <h4 className="font-heading text-sm font-bold text-amber-900 mb-3 flex items-center gap-2">
          ✅ Checklist de dépôt (à cocher au fur et à mesure)
        </h4>
        <ul className="space-y-1 text-sm">
          {CHECKLIST.map(c => (
            <li key={c.id} className="flex items-start gap-2">
              <input type="checkbox" className="mt-1 accent-emerald-600"
                     checked={!!checkedItems[c.id]}
                     onChange={e => setCheckedItems({ ...checkedItems, [c.id]: e.target.checked })}
                     data-testid={`admin-togo-check-${c.id}`} />
              <span className={checkedItems[c.id] ? "line-through text-gray-500" : "text-gray-800"}>{c.label}</span>
            </li>
          ))}
        </ul>
        <div className="mt-3 flex items-center justify-between">
          <div className="text-xs text-gray-600">
            Progression : <b>{Object.values(checkedItems).filter(Boolean).length} / {CHECKLIST.length}</b>
          </div>
          <button onClick={() => setCheckedItems({})} className="text-xs text-red-600 hover:underline">Réinitialiser</button>
        </div>
      </div>

      {previewDoc && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-3"
          onClick={() => setPreviewDoc(null)}
          data-testid="admin-togo-preview-modal"
        >
          <div
            className="bg-white rounded-lg shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-emerald-700 text-white">
              <div className="text-sm font-semibold truncate pr-3">
                👁️ Aperçu — {previewDoc.label} <span className="opacity-70 text-[11px]">({previewDoc.key})</span>
              </div>
              <div className="flex gap-2 items-center">
                <a
                  href={previewDoc.pdf_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[11px] bg-white/20 hover:bg-white/30 px-2 py-1 rounded font-semibold"
                >
                  ⬇️ Ouvrir dans un onglet
                </a>
                <button
                  type="button"
                  onClick={() => setPreviewDoc(null)}
                  className="text-white hover:text-red-200 text-xl leading-none"
                  data-testid="admin-togo-preview-close"
                >
                  ✕
                </button>
              </div>
            </div>
            <iframe
              title={previewDoc.label}
              src={previewDoc.pdf_url}
              className="flex-1 w-full bg-gray-50"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default TogoDossierAdminPanel;
